/**
 * SectionTabs — the tab bar shared by every section page.
 *
 * The sidebar lists nine sections; the pages inside a section are tabs
 * here. Each tab links to an existing route (see ../lib/navigation.ts), so
 * URLs are unchanged. Rendered by the app shell on a section's own pages;
 * detail pages (a single bill, member, board) keep their breadcrumb instead.
 */
import { Link, useLocation } from "react-router-dom";
import { resolveNav, workspacePath, type NavTab } from "../lib/navigation";
import { useWorkspace } from "../hooks/useWorkspace";

function TabRow({ tabs, activeId, workspaceId, label, level }: {
  tabs: NavTab[]; activeId: string | null; workspaceId?: string | null; label: string; level: 1 | 2;
}) {
  return (
    <nav aria-label={label} className={level === 1 ? "cmcp-section-tabs" : "cmcp-section-subtabs"}>
      {tabs.map((tab) => {
        const active = tab.id === activeId;
        return (
          <Link
            key={tab.id}
            to={workspacePath(tab.to, workspaceId)}
            aria-current={active ? "page" : undefined}
            className={active ? "is-active" : undefined}
          >
            {tab.label}
          </Link>
        );
      })}
    </nav>
  );
}

export function SectionTabs() {
  const location = useLocation();
  const { current } = useWorkspace();
  const nav = resolveNav(location.pathname, location.search);
  if (!nav || !nav.isTabPage) return null;
  const { section, tab, child } = nav;
  const showTop = section.tabs.length > 1;
  const showChildren = Boolean(tab.children?.length);
  if (!showTop && !showChildren) return null;
  return (
    <div className="cmcp-section-tabbar" data-section={section.id}>
      {showTop && <TabRow tabs={section.tabs} activeId={tab.id} workspaceId={current?.id} label={`${section.label} pages`} level={1} />}
      {showChildren && <TabRow tabs={tab.children!} activeId={child?.id ?? null} workspaceId={current?.id} label={`${tab.label} views`} level={2} />}
    </div>
  );
}
